// Call controls (design "Meeting room" control bar): round controls center
// (mic/cam/share/react/hand/more + leave pill), elapsed time left, square panel
// toggles right (files/people/chat/tools; Record now lives in Meeting Tools).
// Reads only the local attendee's publish flags + the open panel (§9.3.1).

import { engineSupportsBlur, REACTIONS, Reaction } from '@brizina/call-engine';
import { memo, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { meetingShareLink, readModelBSecret } from '../api';
import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import { callStore, pushToast, setLayout, useCallStore, useCallStoreShallow } from '../store/callStore';
import { IconButton } from '../ui/IconButton';
import {
  IconBlur,
  IconCamera,
  IconCameraOff,
  IconChat,
  IconChevronDown,
  IconDevices,
  IconDocument,
  IconDots,
  IconFile,
  IconFullScreen,
  IconLeave,
  IconLowdata,
  IconMicOff,
  IconMicrophone,
  IconPeople,
  IconShareScreen,
  IconSliders,
  IconSpeakerView,
  IconTools,
} from '../ui/icons';
import type { PanelKind } from './CallView';
import { DeviceMenu } from './DeviceMenu';
import { EqualizerBars } from './EqualizerBars';
import { MeetingCountdown } from './MeetingCountdown';

// Stable kind sets for the per-device carets (mic caret = mic + speaker;
// camera caret = camera). Module-level so DeviceMenu's effect deps stay stable.
const AUDIO_KINDS: MediaDeviceKind[] = ['audioinput', 'audiooutput'];
const VIDEO_KINDS: MediaDeviceKind[] = ['videoinput'];

interface Props {
  slug: string;
  panel: PanelKind;
  onTogglePanel: (kind: Exclude<PanelKind, null>) => void;
}

/** Elapsed call time, isolated so its 1s tick re-renders only this span. */
export const Elapsed = memo(function Elapsed({ className }: { className: string }) {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSec((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const m = String(Math.floor(sec / 60)).padStart(2, '0');
  const s = String(sec % 60).padStart(2, '0');
  return (
    <span className={className}>
      <span className="bz-elapsed-label">Elapsed</span> {m}:{s}
    </span>
  );
});

/** Local mic level as equalizer bars — isolated so speaking-flag flips re-render
 * only these bars, never the whole control bar (§9.3.1). */
const MicMeter = memo(function MicMeter() {
  const speaking = useCallStore((s) =>
    s.localIdentity ? (s.attendees[s.localIdentity]?.isSpeaking ?? false) : false,
  );
  return <EqualizerBars active={speaking} className="bz-ctl-meter" bars={3} />;
});

export function ControlsBar({ slug, panel, onTogglePanel }: Props) {
  probeRender('ControlsBar');
  const navigate = useNavigate();
  // Only the publish flags + hand — NOT the whole local slice, which would
  // re-render the bar on every local speaking/quality flip (§9.3.1).
  const local = useCallStoreShallow((s) => {
    const a = s.localIdentity ? s.attendees[s.localIdentity] : undefined;
    return a
      ? {
          micEnabled: a.micEnabled,
          cameraEnabled: a.cameraEnabled,
          screenShareEnabled: a.screenShareEnabled,
          handRaised: a.handRaisedAt !== null,
        }
      : undefined;
  });
  const isHost = useCallStore((s) => s.isHost);
  const knockCount = useCallStore((s) => s.knocks.length);
  const fileSharingAllowed = useCallStore((s) => s.fileSharingAllowed);
  const layout = useCallStore((s) => s.layout);

  const [deviceMenu, setDeviceMenu] = useState<null | 'audio' | 'video' | 'all'>(null);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Low-bandwidth mode (F-CALL-7): sticky per session via localStorage.
  const [lowBw, setLowBw] = useState(() => window.localStorage.getItem('bz-low-bandwidth') === '1');
  useEffect(() => {
    engine.setLowBandwidth(lowBw);
    window.localStorage.setItem('bz-low-bandwidth', lowBw ? '1' : '0');
  }, [lowBw]);
  const toggleLowBw = useCallback(() => {
    setLowBw((v) => !v);
    setMoreOpen(false);
  }, []);

  // Background blur (F-CALL-5): hidden where unsupported; sticky per session.
  const blurAvailable = engineSupportsBlur();
  const [blur, setBlur] = useState(() => window.localStorage.getItem('bz-blur') === '1');
  const toggleBlur = useCallback(() => {
    setMoreOpen(false);
    setBlur((v) => {
      const next = !v;
      window.localStorage.setItem('bz-blur', next ? '1' : '0');
      engine.setBackgroundBlur(next).catch(() => pushToast('Could not start blur', 'warning'));
      return next;
    });
  }, []);
  // Apply persisted blur once the camera is live.
  useEffect(() => {
    if (blur && local?.cameraEnabled) void engine.setBackgroundBlur(true).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local?.cameraEnabled]);

  const toggleMic = useCallback(() => {
    const me = engine.snapshots().find((a) => a.isLocal);
    const turningOn = !me?.micEnabled;
    void engine.setMicEnabled(turningOn).then(() => {
      // F-COM-4: unmuting auto-lowers your hand.
      if (turningOn && engine.localIdentity && engine.isHandRaised(engine.localIdentity)) {
        void engine.setHandRaised(false);
      }
    });
  }, []);
  const toggleCam = useCallback(() => {
    void engine.setCameraEnabled(!engine.snapshots().find((a) => a.isLocal)?.cameraEnabled);
  }, []);
  const toggleShare = useCallback(() => {
    const sharing = engine.snapshots().find((a) => a.isLocal)?.screenShareEnabled;
    engine.setScreenShareEnabled(!sharing).catch((e: unknown) => {
      // Cancelling the browser picker is not an error worth surfacing.
      if (e instanceof Error && e.name !== 'NotAllowedError') pushToast(e.message, 'error');
    });
  }, []);
  const toggleHand = useCallback(() => {
    const raised = engine.localIdentity ? engine.isHandRaised(engine.localIdentity) : false;
    void engine.setHandRaised(!raised);
  }, []);
  const copyLink = useCallback(() => {
    setMoreOpen(false);
    // Shared builder: carries the Model B key (#k=…) for zero-knowledge meetings
    // so the copied link actually works; a no-op fragment for Model A.
    void navigator.clipboard
      .writeText(meetingShareLink(`/m/${slug}`, readModelBSecret()))
      .then(() => pushToast('Invite link copied'));
  }, [slug]);
  const toggleLayout = useCallback(() => {
    setMoreOpen(false);
    setLayout(callStore.getState().layout === 'grid' ? 'speaker' : 'grid');
  }, []);
  const fullscreen = useCallback(() => {
    setMoreOpen(false);
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => {});
  }, []);
  const leave = useCallback(() => {
    void engine.disconnect().then(() => navigate('/'));
  }, [navigate]);

  const openAllDevices = useCallback(() => {
    setMoreOpen(false);
    setDeviceMenu((m) => (m === 'all' ? null : 'all'));
  }, []);
  const openMicDevices = useCallback(() => {
    setReactionsOpen(false);
    setMoreOpen(false);
    setDeviceMenu((m) => (m === 'audio' ? null : 'audio'));
  }, []);
  const openCamDevices = useCallback(() => {
    setReactionsOpen(false);
    setMoreOpen(false);
    setDeviceMenu((m) => (m === 'video' ? null : 'video'));
  }, []);
  const closeDeviceMenu = useCallback(() => setDeviceMenu(null), []);
  const toggleReactions = useCallback(() => {
    setMoreOpen(false);
    setReactionsOpen((v) => !v);
  }, []);
  const toggleMore = useCallback(() => {
    setReactionsOpen(false);
    setMoreOpen((v) => !v);
  }, []);
  const chatUnread = useCallStore((s) => s.chatUnread);
  const openChat = useCallback(() => onTogglePanel('chat'), [onTogglePanel]);
  const openPeople = useCallback(() => onTogglePanel('people'), [onTogglePanel]);
  const openFiles = useCallback(() => onTogglePanel('files'), [onTogglePanel]);
  const openTools = useCallback(() => onTogglePanel('tools'), [onTogglePanel]);
  const openHost = useCallback(() => onTogglePanel('host'), [onTogglePanel]);

  const sendReaction = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    const emoji = e.currentTarget.dataset.emoji as Reaction;
    if (!engine.sendReaction(emoji)) pushToast('Easy on the reactions 🙂', 'warning');
    setReactionsOpen(false);
  }, []);

  return (
    <div className="bz-controls">
      <Elapsed className="bz-controls-info bz-hide-mobile" />
      <div className="bz-controls-middle">
        {/* Mic pill: live audio bars idle, revealing the mic-select caret on hover. */}
        <div className="bz-ctl-split bz-ctl-mic" data-active={deviceMenu === 'audio' ? '' : undefined}>
          <span className="bz-ctl-lead">
            {local?.micEnabled && <MicMeter />}
            <button
              type="button"
              className="bz-ctl-caret"
              onClick={openMicDevices}
              aria-label="Change microphone"
              title="Change microphone"
            >
              <IconChevronDown size={12} />
            </button>
          </span>
          <IconButton
            label={local?.micEnabled ? 'Mute' : 'Unmute'}
            tone={local?.micEnabled ? 'default' : 'danger'}
            onClick={toggleMic}
          >
            {local?.micEnabled ? <IconMicrophone size={18} /> : <IconMicOff size={18} />}
          </IconButton>
        </div>
        {/* Camera pill: caret revealed on hover (no audio to show). */}
        <div className="bz-ctl-split" data-active={deviceMenu === 'video' ? '' : undefined}>
          <span className="bz-ctl-lead">
            <button
              type="button"
              className="bz-ctl-caret"
              onClick={openCamDevices}
              aria-label="Change camera"
              title="Change camera"
            >
              <IconChevronDown size={12} />
            </button>
          </span>
          <IconButton
            label={local?.cameraEnabled ? 'Stop camera' : 'Start camera'}
            tone={local?.cameraEnabled ? 'default' : 'danger'}
            onClick={toggleCam}
          >
            {local?.cameraEnabled ? <IconCamera size={18} /> : <IconCameraOff size={18} />}
          </IconButton>
        </div>
        <IconButton
          className="bz-hide-mobile"
          label={local?.screenShareEnabled ? 'Stop sharing' : 'Share screen'}
          tone={local?.screenShareEnabled ? 'active' : 'default'}
          onClick={toggleShare}
        >
          <IconShareScreen size={18} />
        </IconButton>
        <div className="bz-ctl-wrap bz-hide-mobile">
          <IconButton label="Reactions" onClick={toggleReactions}>
            <span className="bz-emoji">😊</span>
          </IconButton>
          {reactionsOpen && (
            <div className="bz-reactions-menu" role="menu">
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="bz-reaction-btn"
                  data-emoji={emoji}
                  onClick={sendReaction}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        <IconButton
          label={local?.handRaised ? 'Lower hand' : 'Raise hand'}
          tone={local?.handRaised ? 'warning' : 'default'}
          onClick={toggleHand}
        >
          <span className="bz-emoji">✋</span>
        </IconButton>
        <div className="bz-ctl-wrap">
          <IconButton label="More options" tone={moreOpen ? 'active' : 'default'} onClick={toggleMore}>
            <IconDots size={18} />
          </IconButton>
          {moreOpen && (
            <div className="bz-menu bz-more-menu" role="menu">
              {blurAvailable && (
                <button type="button" className="bz-menu-item" onClick={toggleBlur}>
                  <span className="bz-menu-item-icon">
                    <IconBlur size={16} />
                  </span>
                  <span className="bz-menu-item-label">Blur my background</span>
                  {blur && <span className="bz-menu-on">ON</span>}
                </button>
              )}
              <button type="button" className="bz-menu-item" onClick={toggleLowBw}>
                <span className="bz-menu-item-icon">
                  <IconLowdata size={16} />
                </span>
                <span className="bz-menu-item-label">Low-data mode</span>
                {lowBw && <span className="bz-menu-on">ON</span>}
              </button>
              <button type="button" className="bz-menu-item" onClick={toggleLayout}>
                <span className="bz-menu-item-icon">
                  <IconSpeakerView size={16} />
                </span>
                <span className="bz-menu-item-label">
                  {layout === 'grid' ? 'Speaker view' : 'Grid view'}
                </span>
              </button>
              <button type="button" className="bz-menu-item" onClick={fullscreen}>
                <span className="bz-menu-item-icon">
                  <IconFullScreen size={16} />
                </span>
                <span className="bz-menu-item-label">Fullscreen</span>
              </button>
              <button type="button" className="bz-menu-item" onClick={openAllDevices}>
                <span className="bz-menu-item-icon">
                  <IconDevices size={16} />
                </span>
                <span className="bz-menu-item-label">Devices</span>
              </button>
              <button type="button" className="bz-menu-item" onClick={copyLink}>
                <span className="bz-menu-item-icon">
                  <IconFile size={16} />
                </span>
                <span className="bz-menu-item-label">Copy invite link</span>
              </button>
            </div>
          )}
        </div>
        <IconButton
          className="bz-mobile-only"
          label="People"
          tone={panel === 'people' ? 'active' : 'default'}
          onClick={openPeople}
        >
          <IconPeople size={19} />
          {knockCount > 0 && <span className="bz-iconbtn-badge">{knockCount}</span>}
        </IconButton>
        <button type="button" className="bz-leave-btn" onClick={leave} title="Leave call" aria-label="Leave">
          <IconLeave size={20} />
        </button>
        {deviceMenu && (
          <DeviceMenu
            kinds={
              deviceMenu === 'audio' ? AUDIO_KINDS : deviceMenu === 'video' ? VIDEO_KINDS : undefined
            }
            onClose={closeDeviceMenu}
          />
        )}
      </div>
      <div className="bz-controls-panels bz-hide-mobile">
        <MeetingCountdown />
        {fileSharingAllowed && (
          <IconButton
            shape="square"
            label="Files"
            tone={panel === 'files' ? 'active' : 'default'}
            onClick={openFiles}
          >
            <IconDocument size={18} />
          </IconButton>
        )}
        <IconButton
          shape="square"
          label="People"
          tone={panel === 'people' ? 'active' : 'default'}
          onClick={openPeople}
        >
          <IconPeople size={18} />
          {knockCount > 0 && <span className="bz-iconbtn-badge">{knockCount}</span>}
        </IconButton>
        <IconButton
          shape="square"
          label="Chat"
          tone={panel === 'chat' ? 'active' : 'default'}
          onClick={openChat}
        >
          <IconChat size={18} />
          {chatUnread > 0 && panel !== 'chat' && <span className="bz-iconbtn-dot" />}
        </IconButton>
        {isHost && (
          <IconButton
            shape="square"
            label="Host controls"
            tone={panel === 'host' ? 'active' : 'default'}
            onClick={openHost}
          >
            <IconSliders size={18} />
          </IconButton>
        )}
        {/* Always available: the panel shows Record/Timer disabled for
            attendees, so everyone can open it (attendees vote on polls there). */}
        <IconButton
          shape="square"
          label="Meeting tools"
          tone={panel === 'tools' ? 'active' : 'default'}
          onClick={openTools}
        >
          <IconTools size={18} />
        </IconButton>
      </div>
    </div>
  );
}
